import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { Button } from '../../components/ui/Button'
import { Notice, Spinner } from '../../components/ui/misc'
import { useMe } from '../../lib/api'
import { authClient } from '../../lib/auth'
import { AuthLayout, authErrorMessage } from './AuthLayout'

export function AcceptInvitePage() {
  const { id = '' } = useParams()
  const { data: me, isLoading } = useMe()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const qc = useQueryClient()
  const next = `/invite/${id}`

  if (isLoading) return <Spinner />
  if (!me?.user)
    return (
      <AuthLayout title="You've been invited" subtitle="Sign in or create an account with the email address the invitation was sent to.">
        <div className="flex flex-col gap-2">
          <Link to={`/signup?next=${encodeURIComponent(next)}`} className="btn-primary h-10">
            Create account
          </Link>
          <Link to={`/login?next=${encodeURIComponent(next)}`} className="btn-secondary h-10">
            Sign in
          </Link>
        </div>
      </AuthLayout>
    )

  const act = async (accept: boolean) => {
    setBusy(true)
    setError(null)
    const { error: err } = accept
      ? await authClient.organization.acceptInvitation({ invitationId: id })
      : await authClient.organization.rejectInvitation({ invitationId: id })
    setBusy(false)
    if (err) return setError(authErrorMessage(err))
    await qc.invalidateQueries()
    navigate('/home')
  }

  return (
    <AuthLayout title="Join the organization" subtitle={`Signed in as ${me.user.email}.`}>
      {error && (
        <div className="mb-3">
          <Notice tone="warning">{error} The invitation must match the email you are signed in with.</Notice>
        </div>
      )}
      <div className="flex gap-2">
        <Button className="h-10 flex-1" loading={busy} onClick={() => act(true)}>
          Accept invitation
        </Button>
        <Button variant="secondary" className="h-10" disabled={busy} onClick={() => act(false)}>
          Decline
        </Button>
      </div>
    </AuthLayout>
  )
}
